import React from "react";
import {
  Box,
  Button,
  Typography,
  Chip,
  Paper,
  Stack,
  alpha,
} from "@mui/material";
import { AddCircle, DeleteOutline, Schedule } from "@mui/icons-material";
import CustomSelect from "../../../../../FormFields/CustomSelect/CustomSelect";
import CustomTimePicker from "../../../../../FormFields/TiimePicker/TimePicker";
import SearchableSelect from "../../../../../FormFields/CustomSelect/SearchableSelect";
import { frequencyOptions } from "../../medication.config";

const daysOfWeek = [
  { value: "Mon", label: "Mon" },
  { value: "Tue", label: "Tue" },
  { value: "Wed", label: "Wed" },
  { value: "Thu", label: "Thu" },
  { value: "Fri", label: "Fri" },
  { value: "Sat", label: "Sat" },
  { value: "Sun", label: "Sun" },
];

const extractTimeValue = (val) => {
  if (val === null || val === undefined) return "";
  if (typeof val === "string") return val;
  if (val?.target?.value !== undefined) return val.target.value;
  if (val?.value !== undefined) return val.value;
  if (val?.$d && typeof val.format === "function") return val.format("HH:mm:ss");
  return val;
};

const MedicationTimingSection = ({
  formData = {},
  handleChange,
  times = [],
  handleTimeChange,
  setTimes,
  selectedDays = [],
  handleDaySelection,
  visitOptions = [],
  errors = {},
}) => {
  const isWeekly = formData.frequency?.toLowerCase() === "weekly";

  return (
    <Paper
      sx={{
        mt: 3,
        p: 2.5,
        bgcolor: "#ffffff",
        border: "1.5px solid #e2e8f0",
        borderRadius: 2.5,
      }}
    >
      <Stack spacing={2.5}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2.5 }}>
          <Box sx={{ flex: "1 1 40%", minWidth: "240px" }}>
            <CustomSelect
              label="Frequency"
              name="frequency"
              options={frequencyOptions}
              value={formData.frequency}
              onChange={handleChange}
              error={!!errors.frequency}
              helperText={errors.frequency}
              required
              fullWidth
              searchPlaceholder="Search frequency..."
            />
          </Box>

          {isWeekly && (
            <Box sx={{ flex: "1 1 50%", minWidth: "280px" }}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: "#475569",
                  fontSize: "0.78rem",
                }}
              >
                Select Days of Week{" "}
                <Typography component="span" color="#ef4444">
                  *
                </Typography>
              </Typography>
              <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mt: 1 }}>
                {daysOfWeek.map((day) => (
                  <Chip
                    key={day.value}
                    label={day.label}
                    onClick={() => handleDaySelection(day.value)}
                    color={
                      selectedDays.includes(day.value) ? "primary" : "default"
                    }
                    sx={{
                      fontWeight: selectedDays.includes(day.value) ? 700 : 500,
                      fontSize: "0.72rem",
                      "&.MuiChip-colorPrimary": {
                        bgcolor: "#2469F0",
                        color: "white",
                      },
                    }}
                    size="small"
                  />
                ))}
              </Box>
              {errors.weekdays && (
                <Typography
                  color="error"
                  sx={{ fontSize: "0.72rem", mt: 0.5 }}
                >
                  {errors.weekdays}
                </Typography>
              )}
            </Box>
          )}
        </Box>

        <Box>
          <Typography
            sx={{
              fontWeight: 700,
              color: "#0f172a",
              mb: 1.5,
              fontSize: "0.82rem",
              display: "flex",
              alignItems: "center",
              gap: 0.75,
            }}
          >
            <Schedule sx={{ fontSize: 18, color: "#2469F0" }} />
            Administration Times & Visits
          </Typography>

          <Stack spacing={1.5}>
            {times.map((t, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 2,
                  alignItems: "flex-start",
                }}
              >
                <Box sx={{ flex: "1 1 25%", minWidth: "160px" }}>
                  <CustomTimePicker
                    label={`Time ${index + 1}`}
                    name={`time_${index}`}
                    value={t.time}
                    onChange={(val) =>
                      handleTimeChange(index, "time", extractTimeValue(val))
                    }
                    required
                    fullWidth
                  />
                </Box>
                <Box sx={{ flex: "1 1 40%", minWidth: "220px" }}>
                  <SearchableSelect
                    options={visitOptions}
                    placeholder="Select Visit..."
                    value={t.visit}
                    onChange={(val) => handleTimeChange(index, "visit", val)}
                    fullWidth
                    isClearable
                  />
                </Box>
                <Box
                  sx={{
                    flex: "0 0 auto",
                    display: "flex",
                    gap: 0.75,
                    pt: 1,
                  }}
                >
                  <Button
                    size="small"
                    startIcon={<AddCircle sx={{ fontSize: 16 }} />}
                    onClick={() =>
                      setTimes([...times, { time: "", visit: null }])
                    }
                    sx={{
                      color: "#2469F0",
                      fontWeight: 600,
                      textTransform: "none",
                      fontSize: "0.75rem",
                      "&:hover": { bgcolor: alpha("#2469F0", 0.08) },
                    }}
                  >
                    Add
                  </Button>
                  {times.length > 1 && (
                    <Button
                      size="small"
                      startIcon={<DeleteOutline sx={{ fontSize: 16 }} />}
                      onClick={() =>
                        setTimes(times.filter((_, i) => i !== index))
                      }
                      sx={{
                        color: "#dc2626",
                        fontWeight: 600,
                        textTransform: "none",
                        fontSize: "0.75rem",
                        "&:hover": { bgcolor: alpha("#dc2626", 0.08) },
                      }}
                    >
                      Remove
                    </Button>
                  )}
                </Box>
              </Box>
            ))}
          </Stack>
        </Box>
      </Stack>
    </Paper>
  );
};

export default MedicationTimingSection;